import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { ListTechSpecSection, ProductComparisonSection, type TranslatedValue } from "@swo/storefront";
import React, { useMemo } from "react";
import { ReticleSection } from "../../product-technical-specification-page-layout/sections/reticle-section";
import { ColorSection } from "../../product-technical-specification-page-layout/sections/color-section";
import { PlainSection } from "../../product-technical-specification-page-layout/sections/plain-section";
import { transposeValidAttributesListArray } from "./transposeValidAttributesListArray";
import { ListSection } from "../../product-technical-specification-page-layout/sections/list-section";

import { pipe } from "fp-ts/lib/function";
import { array, option } from "fp-ts";

export const RowAttributes: ReactTFC<{
  sectionProps: ProductComparisonSection;
  mode: "all" | "differences-only";
  selectedProductKeys: Array<string>;
}> = props => {
  const { sectionProps } = props;

  switch (sectionProps.tag) {
    case "reticle": {
      return (
        <>
          {sectionProps.attributes.map((productAttributes, idx) => (
            <ReticleSection key={props.selectedProductKeys[idx]} {...sectionProps} attributes={productAttributes} />
          ))}
        </>
      );
    }

    case "color": {
      return (
        <>
          {sectionProps.attributes.map((productAttributes, idx) => (
            <ColorSection key={props.selectedProductKeys[idx]} {...sectionProps} attributes={productAttributes} />
          ))}
        </>
      );
    }

    case "plain": {
      return (
        <>
          {sectionProps.attributes.map((productAttributes, idx) => (
            <PlainSection key={props.selectedProductKeys[idx]} {...sectionProps} attributes={productAttributes} />
          ))}
        </>
      );
    }

    case "list": {
      return (
        <RowListAttributes
          headline={sectionProps.headline}
          tag={sectionProps.tag}
          __key={sectionProps.__key}
          attributes={sectionProps.attributes}
          mode={props.mode}
          selectedProductKeys={props.selectedProductKeys}
        />
      );
    }

    default: {
      const _: never = sectionProps;

      return _;
    }
  }
};

const RowListAttributes: ReactTFC<{
  attributes: Array<ListTechSpecSection["attributes"]>;
  tag: "list";
  headline: string | TranslatedValue;
  __key: string;
  mode: "all" | "differences-only";
  selectedProductKeys: Array<string>;
}> = props => {
  const transposedAttributes = useMemo(() => {
    return transposeValidAttributesListArray(props.attributes);
  }, [props.attributes]);

  const { attributes, ...restOfProps } = props;

  function highlightDifferences(productValues: Array<Array<string>>) {
    const possibleValues = new Set(productValues.map(v => v.join("@")));

    return productValues.map(() => {
      return possibleValues.size > 1 ? true : false;
    });
  }

  return (
    <>
      {transposedAttributes.map((productAttributes, idx) => {
        const differencesHighlight =
          props.mode === "differences-only"
            ? highlightDifferences(productAttributes.map(p => p.values.map(v => (typeof v === "string" ? v : v.value))))
            : undefined;

        return (
          <ListSection
            key={pipe(
              productAttributes.map(a => a.__key),
              array.head,
              option.getOrElseW(() => idx),
            )}
            {...restOfProps}
            attributes={productAttributes}
            differencesHighlight={differencesHighlight}
          />
        );
      })}
    </>
  );
};
